/*
 * Local tourism levy calculator (Scottish Visitor Levy and equivalents).
 *
 * Mirrors backend/pms/visitor_levy.py so the report preview and the PDF/Xero
 * outputs agree — server-side is the fallback, this is the source of truth
 * when the modal is open.
 *
 * Settings are read in camelCase (frontend shape after mapping) with a
 * snake_case fallback so it also works with raw API payloads.
 */

function num(v, d = 0) {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : d
}

function readSetting(settings, camelKey, snakeKey, defaultVal) {
  if (settings == null) return defaultVal
  if (settings[camelKey] !== undefined && settings[camelKey] !== null && settings[camelKey] !== '') {
    return settings[camelKey]
  }
  if (settings[snakeKey] !== undefined && settings[snakeKey] !== null && settings[snakeKey] !== '') {
    return settings[snakeKey]
  }
  return defaultVal
}

function parseDate(v) {
  if (!v) return null
  if (v instanceof Date) return v
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

function dayDiff(a, b) {
  return Math.floor((b - a) / 86400000)
}

function firstField(reservation, ...names) {
  for (const n of names) {
    const v = reservation[n]
    if (v !== undefined && v !== null && v !== '') return v
  }
  return undefined
}

function extractAccommodationExVat(reservation, vatRate, excludeBundledExtras) {
  const explicit = firstField(reservation, 'accommodation_ex_vat', 'accommodationExVat')
  if (explicit !== undefined) return num(explicit)

  let total = num(firstField(reservation, 'accommodationCost', 'accommodation_cost',
                              'total_amount', 'totalAmount', 'totalReservationPrice'))
  if (total <= 0) return 0

  if (excludeBundledExtras) {
    const extraFields = [
      'cleaning_fee', 'cleaningFee', 'breakfast_fee', 'breakfastFee',
      'damage_waiver', 'damageWaiver', 'extras_total', 'extrasTotal',
      'guest_extras', 'guestExtras',
    ]
    for (const f of extraFields) {
      const v = firstField(reservation, f)
      if (v !== undefined) total -= num(v)
    }
  }

  const rate = num(vatRate)
  if (rate > 0) total = total / (1 + rate / 100)
  return total
}

function chargeableNights(reservation, nightCap, liveDate) {
  const totalNights = Math.max(0, Math.floor(num(firstField(reservation, 'total_nights', 'nights', 'totalNights', 'numberOfNights'))))
  if (totalNights <= 0) return { chargeable: 0, total: 0 }

  const checkin = parseDate(firstField(reservation, 'check_in', 'checkIn', 'checkin', 'arrivalDate', 'arrivalTime'))
  const checkout = parseDate(firstField(reservation, 'check_out', 'checkOut', 'checkout', 'departureDate', 'departureTime'))

  let eligible = totalNights
  if (liveDate && checkin && checkout) {
    const effectiveStart = checkin > liveDate ? checkin : liveDate
    const postLive = Math.max(0, dayDiff(effectiveStart, checkout))
    eligible = Math.max(0, Math.min(postLive, totalNights))
  }

  const cap = num(nightCap, null)
  const chargeable = (cap != null && cap > 0) ? Math.min(eligible, Math.floor(cap)) : eligible
  return { chargeable, total: totalNights }
}

export function calculateReservationLevy(reservation, settings) {
  const enabled = readSetting(settings, 'visitorLevyEnabled', 'visitor_levy_enabled', false)
  const zero = {
    gross: 0, netRemitted: 0, providerRetained: 0, vat: 0,
    chargeableNights: 0, totalNights: 0,
  }
  if (!enabled) return zero

  const method = readSetting(settings, 'visitorLevyMethod', 'visitor_levy_method', 'pct')
  const rate = num(readSetting(settings, 'visitorLevyRate', 'visitor_levy_rate', 0))
  const nightCap = readSetting(settings, 'visitorLevyNightCap', 'visitor_levy_night_cap', null)
  const vatRate = num(readSetting(settings, 'visitorLevyVatRate', 'visitor_levy_vat_rate', 0))
  const retentionPct = num(readSetting(settings, 'visitorLevyProviderRetentionPct', 'visitor_levy_provider_retention_pct', 0))
  const excludeBundled = readSetting(settings, 'visitorLevyBundledExtrasExcluded', 'visitor_levy_bundled_extras_excluded', true)
  const liveDate = parseDate(readSetting(settings, 'visitorLevyLiveDate', 'visitor_levy_live_date', null))

  const { chargeable, total: totalNights } = chargeableNights(reservation, nightCap, liveDate)

  let gross = 0
  if (method === 'pct') {
    const base = extractAccommodationExVat(reservation, vatRate, excludeBundled)
    if (totalNights <= 0 || base <= 0 || chargeable <= 0) return zero
    const perNight = base / totalNights
    gross = perNight * chargeable * (rate / 100)
  } else if (method === 'night') {
    if (chargeable <= 0) return zero
    gross = rate * chargeable
  } else if (method === 'booking') {
    if (totalNights <= 0) return zero
    gross = rate
  } else if (method === 'guest') {
    const guests = Math.max(0, Math.floor(num(firstField(reservation, 'total_guests', 'guests', 'numberOfGuests', 'adults'))))
    if (guests <= 0 || totalNights <= 0) return zero
    gross = rate * guests
  } else {
    return zero
  }

  gross = round2(gross)
  if (gross <= 0) return zero

  let vat = 0
  if (vatRate > 0) {
    const divisor = 1 + vatRate / 100
    vat = round2(gross - gross / divisor)
  }
  const retained = round2(gross * retentionPct / 100)
  const remitted = round2(gross - retained - vat)

  return {
    gross,
    netRemitted: remitted,
    providerRetained: retained,
    vat,
    chargeableNights: chargeable,
    totalNights,
  }
}

function round2(n) {
  return Math.round(n * 100) / 100
}

export function aggregateLevy(reservations, settings) {
  const enabled = readSetting(settings, 'visitorLevyEnabled', 'visitor_levy_enabled', false)
  const totals = {
    gross: 0, netRemitted: 0, providerRetained: 0, vat: 0,
    chargeableNights: 0, totalNights: 0,
  }
  const perReservation = []
  if (!enabled || !reservations || reservations.length === 0) {
    return { totals, perReservation, enabled: !!enabled }
  }
  for (const r of reservations) {
    const result = calculateReservationLevy(r, settings)
    if (result.gross <= 0) continue
    totals.gross += result.gross
    totals.netRemitted += result.netRemitted
    totals.providerRetained += result.providerRetained
    totals.vat += result.vat
    totals.chargeableNights += result.chargeableNights
    totals.totalNights += result.totalNights
    perReservation.push({
      guest: firstField(r, 'guest_name', 'guestName', 'name', 'guest') || '',
      chargeable_nights: result.chargeableNights,
      total_nights: result.totalNights,
      gross: result.gross,
    })
  }
  totals.gross = round2(totals.gross)
  totals.netRemitted = round2(totals.netRemitted)
  totals.providerRetained = round2(totals.providerRetained)
  totals.vat = round2(totals.vat)
  return { totals, perReservation, enabled: true }
}
